// src/app.ts — the krzy.ai frontend. Faithful port of the prototype's vanilla
// rendering, refactored to (1) boot from /api/snapshot instead of hardcoded
// literals, (2) reuse the shared model (solve/elast), (3) show provenance
// badges + as_of + a STALE banner, (4) flip edited rows to USER. No framework.
//
// Chart.js is loaded globally (script tag + SRI in index.html), referenced as
// window.Chart — NOT imported as a module.

import {
  elast,
  scoreResult,
  solve,
  type Drivers,
  type HypResult,
  type Result,
  type Source,
  type World,
} from "./model/index.ts";
import type { ScenarioId } from "./model/scenarios.ts";
import { coldStartSnapshot, type SnapshotPayload, type SnapshotProvenance } from "./snapshot.ts";
import { baselineValues, bridge, coldStartBaseline, solveEcon, type EconBaselineValues } from "./econ/index.ts";
import { capexFromParams, capexToParams } from "./urlstate.ts";

/* eslint-disable @typescript-eslint/no-explicit-any */
const Chart: any = (window as any).Chart;

// ---- UI-only constants (not data — data comes from the snapshot's world) ----
const TIERS: Record<string, string> = {
  chips: "Compute & silicon",
  power: "Power & grid equipment",
  util: "Utilities & IPPs",
  gas: "Oil, gas & midstream",
  tele: "Telecom & fiber",
};
const TSHORT: Record<string, string> = { chips: "Silicon", power: "Power", util: "Utility", gas: "Gas", tele: "Fiber" };
const TIERC: Record<string, string> = { chips: "#6250d6", power: "#eb6834", util: "#1baf7a", gas: "#eda100", tele: "#2a78d6" };

interface Ctrl {
  g?: string;
  k?: keyof Drivers;
  lab?: string;
  min?: number;
  max?: number;
  step?: number;
  scale?: number;
  unit?: string;
  hint?: string;
}

const CTRLS: Ctrl[] = [
  { g: "Demand & capex" },
  { k: "capex", lab: "AI capex conviction", min: 20, max: 200, step: 5, scale: 100, unit: "%", hint: "Management willingness to spend, independent of demand. 100% = 2026 guidance." },
  { k: "ai", lab: "AI revenue realisation", min: 20, max: 200, step: 5, scale: 100, unit: "%", hint: "Does AI actually monetise? Below 100% means demand undershoots the build." },
  { k: "oa", lab: "OpenAI solvency", min: 0, max: 100, step: 5, scale: 100, unit: "%", hint: "0% = OpenAI cannot fund its ~$50B of 2026 compute commitments." },
  { k: "an", lab: "Anthropic solvency", min: 0, max: 100, step: 5, scale: 100, unit: "%", hint: "0% = Anthropic cannot fund its $100B AWS and $200B Google Cloud commitments." },
  { g: "Cost of money" },
  { k: "y10", lab: "UST 10-year", min: 1.5, max: 9, step: 0.05, scale: 1, unit: "%", hint: "Benchmark for most corporate issuance. Sept 2026 actual: 4.79%." },
  { k: "y20", lab: "UST 20-year", min: 1.5, max: 9, step: 0.05, scale: 1, unit: "%", hint: "Sept 2026 actual: 5.27%. Utilities and telecoms fund heavily out here." },
  { k: "y30", lab: "UST 30-year", min: 1.5, max: 9, step: 0.05, scale: 1, unit: "%", hint: "Sept 2026 actual: 5.27%. Long-duration grid and fiber assets price off this." },
  { k: "sm", lab: "Credit spread multiple", min: 0.4, max: 4, step: 0.05, scale: 1, unit: "x", hint: "Stress usually rallies Treasuries and widens spreads at the same time. 1.0x = current spreads." },
  { g: "Costs & accounting" },
  { k: "cost", lab: "Input cost index", min: 70, max: 170, step: 5, scale: 100, unit: "%", hint: "Copper, steel, transformers, skilled labour. Compresses supplier margins." },
  { k: "pw", lab: "Power price index", min: 60, max: 220, step: 5, scale: 100, unit: "%", hint: "Wholesale power. Helps merchant IPPs and gas producers, raises hyperscaler operating cost." },
  { k: "gas", lab: "Gas share of new DC power", min: 5, max: 70, step: 1, scale: 100, unit: "%", hint: "Share of incremental data-centre power met by gas rather than grid, nuclear or renewables. Base here is 25% of the marginal build. Raising this shifts dollars from utilities and IPPs to midstream and producers." },
  { k: "life", lab: "GPU useful life", min: 3, max: 7, step: 0.5, scale: 1, unit: " yrs", hint: "Depreciation schedule. Shorter life = the same capex hits earnings much harder." },
];

// ---- Mutable state (populated by boot()) ----
let world: World;
let S: Drivers;
let SCEN: Record<ScenarioId, { l: string } & Drivers>;
let provenance: SnapshotProvenance;
let econBaseline: EconBaselineValues;
let active = "base";
let sortKey = "dRev";
let sortDir = -1;
let revChart: any = null;
let betaChart: any = null;
let rbetaChart: any = null;
let curveChart: any = null;

/** Fields the user has overridden. Keys: `driver:y10`, `sup:NVIDIA:rev`,
 *  `hyp:orcl:capex`, `coef:sticky`. Overridden rows/sliders show a USER badge. */
const userOverrides = new Set<string>();

// ---- formatting ----
function fmtB(v: number, d = 1): string {
  const s = v < 0 ? "-" : "";
  return s + "$" + Math.abs(v).toFixed(d) + "B";
}
function fmtPct(v: number, d = 1): string {
  return (v >= 0 ? "+" : "") + (v * 100).toFixed(d) + "%";
}
function cls(v: number): string {
  return v > 0.0005 ? "pos" : v < -0.0005 ? "neg" : "neu";
}

// ---- provenance badges ----
function provLabel(s: Source): string {
  return s === "ESTIMATE" ? "EST" : s;
}
function provColor(s: Source): string {
  switch (s) {
    case "LIVE": return "var(--pos)";
    case "FILED": return "var(--acc)";
    case "STALE": return "var(--neg)";
    case "USER": return "var(--warn)";
    default: return "var(--ink3)";
  }
}
function provBadge(s: Source): string {
  const c = provColor(s);
  return '<span class="tag" title="source: ' + s + '" style="background:' + c + "22;color:" + c + '">' + provLabel(s) + "</span>";
}
function supSource(name: string): Source {
  for (const k of userOverrides) if (k.startsWith("sup:" + name + ":")) return "USER";
  return provenance.entities[name]?.revenue?.source ?? "ESTIMATE";
}
function hypSource(id: string): Source {
  for (const k of userOverrides) if (k.startsWith("hyp:" + id + ":")) return "USER";
  return provenance.entities[id]?.capex?.source ?? "GUIDANCE";
}
function driverSource(k: keyof Drivers): Source | null {
  if (userOverrides.has("driver:" + k)) return "USER";
  switch (k) {
    case "y10": return provenance.yields.y10.source;
    case "y20": return provenance.yields.y20.source;
    case "y30": return provenance.yields.y30.source;
    case "sm": return provenance.spreadMultiple.source;
    case "gas": return provenance.gasShare.source;
    case "pw": return provenance.powerPrice.source;
    default: return null;
  }
}

// ---- controls ----
function buildControls(): void {
  const el = document.getElementById("controls")!;
  el.innerHTML = "";
  CTRLS.forEach((c) => {
    if (c.g) {
      const g = document.createElement("div");
      g.className = "ghead";
      g.textContent = c.g;
      el.appendChild(g);
      return;
    }
    const key = c.k!;
    const scale = c.scale!;
    const unit = c.unit ?? "";
    const d = document.createElement("div");
    d.className = "ctl";
    const val = S[key] * scale;
    const disp = scale === 1 ? (key.charAt(0) === "y" ? val.toFixed(2) : key === "sm" ? val.toFixed(2) : val) : Math.round(val);
    const src = driverSource(key);
    const srcTag = src ? " " + provBadge(src) : "";
    d.innerHTML =
      '<label><span>' + c.lab + srcTag + '</span><b id="v_' + key + '">' + disp + unit + "</b></label>" +
      '<input type="range" id="r_' + key + '" min="' + c.min + '" max="' + c.max + '" step="' + c.step + '" value="' + val + '">' +
      '<div class="hint">' + c.hint + "</div>";
    el.appendChild(d);
    d.querySelector("input")!.addEventListener("input", (e) => {
      const target = e.target as HTMLInputElement;
      S[key] = parseFloat(target.value) / scale;
      const v = S[key] * scale;
      document.getElementById("v_" + key)!.textContent =
        (scale === 1 ? (key.charAt(0) === "y" || key === "sm" ? v.toFixed(2) : v) : Math.round(v)) + unit;
      userOverrides.add("driver:" + key);
      active = "custom";
      buildControls(); // refresh so the moved slider's badge flips to USER
      renderChips();
      render();
    });
  });
}

function renderChips(): void {
  const el = document.getElementById("chips")!;
  el.innerHTML = "";
  (Object.keys(SCEN) as ScenarioId[]).forEach((k) => {
    const b = document.createElement("button");
    b.className = "chip" + (active === k ? " on" : "");
    b.textContent = SCEN[k].l;
    b.onclick = () => {
      S = { ...SCEN[k] };
      active = k;
      for (const x of [...userOverrides]) if (x.startsWith("driver:")) userOverrides.delete(x);
      buildControls();
      renderChips();
      render();
    };
    el.appendChild(b);
  });
  if (active === "custom") {
    const c = document.createElement("button");
    c.className = "chip on";
    c.textContent = "Custom";
    el.appendChild(c);
  }
}

// ---- KPIs ----
function renderKPIs(r: Result): void {
  let supRev = 0, supRevB = 0, supProf = 0, supProfB = 0;
  r.sup.forEach((s) => { supRev += s.rev; supRevB += s.revBase; supProf += s.profit; supProfB += s.profitBase; });
  let hypProf = 0, hypProfB = 0, dep = 0, depB = 0, intr = 0, intrB = 0, debt = 0;
  r.hyp.forEach((h) => { hypProf += h.profit; hypProfB += h.profitBase; dep += h.dep; depB += h.depBase; intr += h.interest; intrB += h.interest0; debt += h.newDebt; });
  let pRev = 0, pRevB = 0, gRev = 0, gRevB = 0;
  r.sup.forEach((s) => {
    if (s.t === "power" || s.t === "util") { pRev += s.rev; pRevB += s.revBase; }
    if (s.t === "gas") { gRev += s.rev; gRevB += s.revBase; }
  });

  const K = [
    { k: "System capex", v: fmtB(r.sysCapex, 0), d: fmtPct(r.cx - 1), c: cls(r.cx - 1) },
    { k: "Debt-funded capex", v: fmtB(debt, 0), d: Math.round((debt / r.sysCapex) * 100) + "% of build", c: "neu" },
    { k: "Interest on new debt", v: fmtB(intr, 1), d: fmtB(intr - intrB, 1) + " vs base", c: cls(intrB - intr) },
    { k: "Annual depreciation", v: fmtB(dep, 0), d: fmtB(dep - depB, 0) + " vs 6-yr", c: cls(depB - dep) },
    { k: "Supplier revenue", v: fmtB(supRev, 0), d: fmtB(supRev - supRevB, 0) + " vs base", c: cls(supRev - supRevB) },
    { k: "Supplier profit", v: fmtB(supProf, 0), d: fmtPct(supProf / supProfB - 1), c: cls(supProf - supProfB) },
    { k: "Grid & equipment rev", v: fmtB(pRev, 0), d: fmtB(pRev - pRevB, 0) + " vs base", c: cls(pRev - pRevB) },
    { k: "Oil & gas rev", v: fmtB(gRev, 0), d: fmtB(gRev - gRevB, 0) + " vs base", c: cls(gRev - gRevB) },
    { k: "Hyperscaler profit", v: fmtB(hypProf, 0), d: fmtPct(hypProf / hypProfB - 1), c: cls(hypProf - hypProfB) },
  ];
  document.getElementById("kpis")!.innerHTML = K.map((x) =>
    '<div class="kpi"><div class="k">' + x.k + '</div><div class="v">' + x.v + '</div><div class="d ' + x.c + '">' + x.d + "</div></div>",
  ).join("");
}

// ---- Principle Three: the future-mortgage scoreboard ----
// The NexusOps Sandbox reading — the depreciation wall read as an
// intergenerational-mortgage test. Derived and interpretive; presented as such.
function scoreColor(v: number): string {
  // 0–100, higher = more mortgaged = worse. Green → amber → red.
  return v >= 60 ? "var(--neg)" : v >= 40 ? "var(--warn)" : "var(--pos)";
}
function renderPrincipleThree(r: Result): void {
  const el = document.getElementById("principle3");
  if (!el) return;
  const s = scoreResult(r);
  const sys = s.systemScore;
  const cov = s.systemCoverage;
  const col = scoreColor(sys);
  const covLabel = cov >= 1
    ? "profit covers the locked burden " + cov.toFixed(2) + "×"
    : "the locked burden exceeds profit — coverage " + cov.toFixed(2) + "×";

  const bars = s.entities.map((e) => {
    const c = scoreColor(e.score);
    const comp = e.components;
    return '<div class="p3row">' +
      '<span class="p3n">' + e.name + ' <span class="tag">' + e.rating + "</span></span>" +
      '<span class="p3bar"><span class="p3fill" style="width:' + e.score.toFixed(0) + "%;background:" + c + '"></span></span>' +
      '<span class="p3s" style="color:' + c + '">' + e.score.toFixed(0) + "</span>" +
      '<span class="p3d">burden ' + fmtB(e.burden, 0) + " vs profit " + fmtB(e.profit, 0) +
      " · frag " + (comp.fragility * 100).toFixed(0) + " / lock " + (comp.lock * 100).toFixed(0) +
      " / lev " + (comp.leverage * 100).toFixed(0) + "</span></div>";
  }).join("");

  el.innerHTML =
    '<h2>Principle Three — the future with a seat at the table</h2>' +
    '<div class="sub" style="font-size:13px;margin-bottom:14px">A reading from the ' +
    '<a href="https://sepahsalar.org/analysis/nexusops" style="color:var(--acc);text-decoration:none">NexusOps</a> sequence: ' +
    'the depreciation wall read as an intergenerational-mortgage test. Higher = more of the future mortgaged for the present — ' +
    'a debt-financed, locked build whose burden does not unwind when the revenue that covers it does. Derived and interpretive, ' +
    'like the model’s estimated parameters, not a filed figure.</div>' +
    '<div class="p3head">' +
      '<div class="p3score"><div class="p3big" style="color:' + col + '">' + sys.toFixed(0) + '<span class="p3max">/100</span></div>' +
        '<div class="p3lab">Future-mortgage score</div></div>' +
      '<div class="p3cov"><div class="p3covv" style="color:' + (cov >= 1 ? "var(--pos)" : "var(--neg)") + '">' + cov.toFixed(2) + '×</div>' +
        '<div class="p3lab">System coverage — ' + covLabel + '</div></div>' +
    '</div>' +
    '<div class="p3list">' + bars + "</div>" +
    '<div class="note" style="margin-top:12px">' + s.note + '</div>';
}

// ---- Treasury curve chart ----
function renderCurve(): void {
  const ctx = document.getElementById("curveChart");
  if (curveChart) curveChart.destroy();
  const BY = world.BASE_Y;
  curveChart = new Chart(ctx, {
    type: "line",
    data: {
      labels: ["10y", "20y", "30y"],
      datasets: [
        { label: "Scenario", data: [S.y10, S.y20, S.y30], borderColor: "#2a78d6", backgroundColor: "#2a78d6", borderWidth: 2, tension: 0.25, pointRadius: 5, pointBorderWidth: 2 },
        { label: "Calibration base", data: [BY.y10, BY.y20, BY.y30], borderColor: "#898781", borderWidth: 1.5, borderDash: [5, 4], tension: 0.25, pointRadius: 3 },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: "bottom", labels: { boxWidth: 10, boxHeight: 10, usePointStyle: false, font: { size: 11.5 } } },
        tooltip: { callbacks: { label: (c: any) => c.dataset.label + ": " + c.parsed.y.toFixed(2) + "%" } },
      },
      scales: {
        x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 11.5 } } },
        y: { grid: { color: "rgba(128,128,128,.16)" }, border: { display: false }, ticks: { font: { size: 11.5 }, callback: (v: number) => v.toFixed(1) + "%" } },
      },
    },
  });
}

// ---- cost-of-debt ladder ----
function renderLadder(r: Result): void {
  const rows = r.hyp.map((h) => ({ n: h.name, rt: h.rating, w: h.wacd, w0: h.wacd0, c: "#2a78d6" }));
  ["Constellation", "Vistra", "Talen Energy", "NextEra", "Williams", "Energy Transfer", "EQT", "AT&T", "Lumen", "Vertiv", "GE Vernova"].forEach((nm) => {
    const s = r.sup.find((x) => x.n === nm);
    if (s) rows.push({ n: nm, rt: s.rt, w: s.wacd, w0: s.wacd0, c: TIERC[s.t] });
  });
  rows.sort((a, b) => b.w - a.w);
  const mx = rows[0].w;
  document.getElementById("ladder")!.innerHTML = rows.map((x) => {
    const d = x.w - x.w0;
    return '<div class="lr"><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + x.n +
      ' <span class="tag">' + x.rt + "</span></span>" +
      '<span><span class="lb" style="display:block;width:' + ((x.w / mx) * 100).toFixed(1) + "%;background:" + x.c + '"></span></span>' +
      '<span style="text-align:right;font-variant-numeric:tabular-nums">' + (x.w * 100).toFixed(2) + '%<br><span style="font-size:10.5px" class="' + cls(-d) + '">' + (d >= 0 ? "+" : "") + Math.round(d * 10000) + "bp</span></span></div>";
  }).join("");
}

// ---- flow diagram ----
function renderFlow(r: Result): void {
  const W = 980, H = 430, PAD = 26;
  const colX = [16, 300, 660], colW = [132, 152, 152];
  const labVals = [r.labs.openai, r.labs.anthropic, r.labs.other];
  const labNames = ["OpenAI", "Anthropic", "Other / enterprise"];
  const labTot = labVals.reduce((a, b) => a + b, 0) || 1;
  const hypVals = r.hyp.map((h) => h.capex);
  const hypTot = hypVals.reduce((a, b) => a + b, 0) || 1;
  const tk: Array<"chips" | "power" | "util" | "gas" | "tele" | "other"> = ["chips", "power", "util", "gas", "tele", "other"];
  const tn: Record<string, string> = { chips: "Compute & silicon", power: "Power & grid equip.", util: "Utilities & IPPs", gas: "Oil, gas & midstream", tele: "Telecom & fiber", other: "Land, shells, build" };
  const CAPEX_SPLIT = world.CAPEX_SPLIT;
  const procure = CAPEX_SPLIT.util + CAPEX_SPLIT.gas;
  const split: Record<string, number> = { ...CAPEX_SPLIT };
  split.gas = procure * S.gas;
  split.util = procure * (1 - S.gas);
  const tv = tk.map((k) => r.sysCapex * split[k]);
  const tt = tv.reduce((a, b) => a + b, 0) || 1;

  function lay(vals: number[], tot: number, minH: number): Array<{ y: number; h: number }> {
    const gap = 8, avail = H - PAD * 2 - (vals.length - 1) * gap;
    let hs = vals.map((v) => Math.max(minH, avail * (v / tot)));
    const sum = hs.reduce((a, b) => a + b, 0);
    if (sum > avail) { const f = avail / sum; hs = hs.map((h) => h * f); }
    let y = PAD;
    const out: Array<{ y: number; h: number }> = [];
    hs.forEach((h) => { out.push({ y, h }); y += h + gap; });
    return out;
  }
  const L = lay(labVals, labTot, 20), Hh = lay(hypVals, hypTot, 18), T = lay(tv, tt, 20);
  let s = '<svg class="flowsvg" viewBox="0 0 ' + W + " " + H + '" width="100%" style="display:block" role="img" aria-label="Flow diagram from AI labs through hyperscalers to suppliers"><desc>Compute demand from OpenAI, Anthropic and other labs flows into hyperscaler capital budgets, which flow out to silicon, power, utility and fiber suppliers.</desc>';

  const links: Array<{ a: number; b: number; w: number }> = [];
  world.HYP.forEach((h, hi) => {
    (["openai", "anthropic", "other"] as const).forEach((k, ki) => {
      const wgt = h.mix[k] || (h.aiRev ? 0 : k === "other" ? 1 : 0);
      if (!wgt) return;
      links.push({ a: ki, b: hi, w: wgt * hypVals[hi] });
    });
  });
  const maxLW = Math.max.apply(null, links.map((l) => l.w)) || 1;
  links.forEach((l) => {
    const y1 = L[l.a].y + L[l.a].h / 2, y2 = Hh[l.b].y + Hh[l.b].h / 2;
    const lw = Math.max(1, (l.w / maxLW) * Hh[l.b].h * 0.85);
    const x1 = colX[0] + colW[0], x2 = colX[1], mx = (x1 + x2) / 2;
    s += '<path d="M' + x1 + "," + y1 + " C" + mx + "," + y1 + " " + mx + "," + y2 + " " + x2 + "," + y2 + '" fill="none" stroke="#6250d6" stroke-opacity=".20" stroke-width="' + lw.toFixed(1) + '"/>';
  });
  world.HYP.forEach((_h, hi) => {
    tk.forEach((k, ti) => {
      const w = hypVals[hi] * split[k];
      const y1 = Hh[hi].y + Hh[hi].h / 2, y2 = T[ti].y + T[ti].h / 2;
      const lw = Math.max(0.8, (w / (r.sysCapex * 0.52 || 1)) * T[ti].h * 0.6);
      const x1 = colX[1] + colW[1], x2 = colX[2], mx = (x1 + x2) / 2;
      s += '<path d="M' + x1 + "," + y1 + " C" + mx + "," + y1 + " " + mx + "," + y2 + " " + x2 + "," + y2 + '" fill="none" stroke="' + (TIERC[k] || "#8b8a82") + '" stroke-opacity=".16" stroke-width="' + lw.toFixed(1) + '"/>';
    });
  });

  function node(x: number, w: number, o: { y: number; h: number }, label: string, val: string, color: string, sub?: string): string {
    let t = '<rect x="' + x + '" y="' + o.y + '" width="' + w + '" height="' + o.h + '" rx="4" fill="' + color + '" fill-opacity=".14" stroke="' + color + '" stroke-opacity=".5" stroke-width=".8"/>';
    t += '<text x="' + (x + 9) + '" y="' + (o.y + (o.h > 32 ? 17 : o.h / 2 + 4)) + '" font-size="12.5" fill="currentColor">' + label + "</text>";
    if (o.h > 32) t += '<text x="' + (x + 9) + '" y="' + (o.y + 33) + '" font-size="11.5" fill="currentColor" opacity=".62">' + val + (sub ? " · " + sub : "") + "</text>";
    else t += '<text x="' + (x + w - 9) + '" y="' + (o.y + o.h / 2 + 4) + '" font-size="11" text-anchor="end" fill="currentColor" opacity=".62">' + val + "</text>";
    return t;
  }
  labNames.forEach((n, i) => { s += node(colX[0], colW[0], L[i], n, fmtB(labVals[i], 0), "#6250d6"); });
  r.hyp.forEach((h, i) => { s += node(colX[1], colW[1], Hh[i], h.name, fmtB(h.capex, 0), "#2a78d6", fmtPct(h.capex / h.capexBase - 1, 0)); });
  tk.forEach((k, i) => { s += node(colX[2], colW[2], T[i], tn[k], fmtB(tv[i], 0), TIERC[k] || "#8b8a82"); });
  s += '<text x="' + colX[0] + '" y="16" font-size="11" fill="currentColor" opacity=".5">COMPUTE DEMAND</text>';
  s += '<text x="' + colX[1] + '" y="16" font-size="11" fill="currentColor" opacity=".5">CAPEX CARRIERS</text>';
  s += '<text x="' + colX[2] + '" y="16" font-size="11" fill="currentColor" opacity=".5">WHO GETS PAID</text>';
  s += "</svg>";
  document.getElementById("flow")!.innerHTML = s;
}

// ---- revenue impact chart ----
function renderRevChart(r: Result): void {
  const rows = r.sup.map((s) => ({ n: s.n, d: s.rev - s.revBase }))
    .concat(r.hyp.map((h) => ({ n: h.name + " (AI rev)", d: h.aiRev - h.aiRevBase })));
  rows.sort((a, b) => b.d - a.d);
  const ctx = document.getElementById("revChart");
  if (revChart) revChart.destroy();
  revChart = new Chart(ctx, {
    type: "bar",
    data: {
      labels: rows.map((x) => x.n),
      datasets: [{ data: rows.map((x) => x.d), backgroundColor: rows.map((x) => (x.d >= 0 ? "#1baf7a" : "#d03b3b")), borderRadius: 3, maxBarThickness: 16 }],
    },
    options: {
      indexAxis: "y", responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c: any) => fmtB(c.parsed.x) } } },
      scales: {
        x: { grid: { color: "rgba(128,128,128,.16)" }, border: { display: false }, ticks: { font: { size: 11.5 }, callback: (v: number) => fmtB(v, 0) } },
        y: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 11.5 }, autoSkip: false } },
      },
    },
  });
}

// ---- beta charts ----
function renderBetas(): void {
  const cb = world.SUP.map((s) => ({ n: s.n, v: elast(s.n, "capex", 0.1, true, S, world), t: s.t })).sort((a, b) => b.v - a.v);
  const rb = world.SUP.map((s) => ({ n: s.n, v: elast(s.n, "y10", 0.5, false, S, world), t: s.t })).sort((a, b) => a.v - b.v);
  function mk(id: string, rows: Array<{ n: string; v: number; t: string }>, ref: any, fmt: (v: number) => string): any {
    const ctx = document.getElementById(id);
    if (ref && ref.destroy) ref.destroy();
    return new Chart(ctx, {
      type: "bar",
      data: {
        labels: rows.map((x) => x.n),
        datasets: [{ data: rows.map((x) => x.v), backgroundColor: rows.map((x) => TIERC[x.t]), borderRadius: 3, maxBarThickness: 18 }],
      },
      options: {
        indexAxis: "y", responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c: any) => fmt(c.parsed.x) } } },
        scales: {
          x: { grid: { color: "rgba(128,128,128,.16)" }, border: { display: false }, ticks: { font: { size: 11 }, callback: fmt } },
          y: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 11 }, autoSkip: false } },
        },
      },
    });
  }
  betaChart = mk("betaChart", cb, betaChart, (v) => (+v).toFixed(2));
  rbetaChart = mk("rbetaChart", rb, rbetaChart, (v) => (+v).toFixed(2) + "%");
  const top = cb[0], worst = rb[0];
  document.getElementById("betaNote")!.innerHTML =
    "Highest capex beta: <b>" + top.n + "</b> at " + top.v.toFixed(2) +
    ". Most rate-exposed: <b>" + worst.n + "</b>, losing " + Math.abs(worst.v).toFixed(2) + "% of revenue per 100bp. " +
    "Note that these rank differently — capex beta is about who sells into the build, rate beta is about who <i>funds</i> their own build. " +
    "Regulated utilities and levered fiber carry heavy rate beta with modest capex beta; silicon is the mirror image.";
}

// ---- detail table ----
interface TRow {
  t: string; n: string; rt: string; rev: number; revB: number; dRev: number; pct: number;
  m: number; mB: number; p: number; pB: number; dP: number; dc: number; w: number;
}
function renderTable(r: Result): void {
  const rows: TRow[] = r.sup.map((s) => ({
    t: s.t, n: s.n, rt: s.rt, rev: s.rev, revB: s.revBase, dRev: s.rev - s.revBase, pct: s.rev / s.revBase - 1,
    m: s.margin, mB: s.marginBase, p: s.profit, pB: s.profitBase, dP: s.profit - s.profitBase, dc: s.dc, w: s.wacd,
  }));
  const maxAbs = Math.max.apply(null, rows.map((x) => Math.abs(x.dRev))) || 1;
  const cols: Array<[string, string]> = [
    ["n", "Company"], ["dc", "AI exp."], ["w", "Cost of debt"], ["revB", "Base rev"], ["rev", "Scenario rev"],
    ["pct", "Δ rev %"], ["dRev", "Δ rev $"], ["m", "Margin"], ["p", "Profit"], ["dP", "Δ profit"],
  ];
  let h = "<thead><tr>" + cols.map((c) => '<th data-k="' + c[0] + '">' + c[1] + (sortKey === c[0] ? (sortDir < 0 ? " ↓" : " ↑") : "") + "</th>").join("") + "</tr></thead><tbody>";
  if (sortKey === "n") {
    Object.keys(TIERS).forEach((tkk) => {
      const g = rows.filter((x) => x.t === tkk);
      if (!g.length) return;
      h += '<tr class="grp"><td colspan="10">' + TIERS[tkk] + "</td></tr>";
      g.forEach((x) => { h += row(x, maxAbs); });
    });
  } else {
    rows.sort((a, b) => {
      const A = (a as any)[sortKey], B = (b as any)[sortKey];
      return (A < B ? -1 : A > B ? 1 : 0) * sortDir;
    });
    rows.forEach((x) => { h += row(x, maxAbs); });
  }
  const t = document.getElementById("tbl")!;
  t.innerHTML = h + "</tbody>";
  t.querySelectorAll("th").forEach((th) => {
    (th as HTMLElement).onclick = () => {
      const k = th.getAttribute("data-k")!;
      if (sortKey === k) sortDir *= -1;
      else { sortKey = k; sortDir = -1; }
      renderTable(r);
    };
  });
}
function row(x: TRow, maxAbs: number): string {
  const w = (Math.abs(x.dRev) / maxAbs) * 44;
  const bar = '<span class="bar" style="width:' + w.toFixed(1) + "px;background:" + (x.dRev >= 0 ? "var(--pos)" : "var(--neg)") + ';margin-right:6px"></span>';
  return "<tr><td>" + x.n +
    '<span class="tag" style="background:' + TIERC[x.t] + "22;color:" + TIERC[x.t] + '">' + TSHORT[x.t] + "</span>" +
    '<span class="tag">' + x.rt + "</span>" + provBadge(supSource(x.n)) + "</td>" +
    "<td>" + Math.round(x.dc * 100) + "%</td>" +
    "<td>" + (x.w * 100).toFixed(2) + "%</td>" +
    '<td class="neu">' + fmtB(x.revB) + "</td>" +
    "<td>" + fmtB(x.rev) + "</td>" +
    '<td class="' + cls(x.pct) + '">' + fmtPct(x.pct) + "</td>" +
    '<td class="' + cls(x.dRev) + '">' + bar + fmtB(x.dRev) + "</td>" +
    '<td class="' + cls(x.m - x.mB) + '">' + (x.m * 100).toFixed(1) + "%</td>" +
    "<td>" + fmtB(x.p) + "</td>" +
    '<td class="' + cls(x.dP) + '">' + fmtB(x.dP) + "</td></tr>";
}

// ---- narrative ----
function renderNarrative(r: Result): void {
  let supRev = 0, supRevB = 0;
  r.sup.forEach((s) => { supRev += s.rev; supRevB += s.revBase; });
  const sorted = r.sup.slice().sort((a, b) => a.rev / a.revBase - b.rev / b.revBase);
  const worst = sorted[0], best = sorted[sorted.length - 1];
  const h = (id: string): HypResult => r.hyp.find((x) => x.id === id)!;
  const orcl = h("orcl"), msft = h("msft"), amzn = h("amzn"), spcx = h("spcx");
  let intr = 0, intrB = 0;
  r.hyp.forEach((x) => { intr += x.interest; intrB += x.interest0; });
  const dep = r.hyp.reduce((a, x) => a + x.dep, 0);

  const p: string[] = [];
  p.push("<b>Step 1 — demand.</b> OpenAI is funding " + fmtB(r.labs.openai, 0) + " of compute and Anthropic " + fmtB(r.labs.anthropic, 0) +
    ". Everyone else contributes " + fmtB(r.labs.other, 0) + ". That non-lab floor is the most important number in this model: it is what remains if both named labs disappear.");
  p.push("<b>Step 2 — concentration.</b> Oracle books roughly 75% of its AI revenue against OpenAI; Microsoft about 60%. Amazon and Google are anchored to Anthropic instead. Here Oracle's capex lands at " + fmtB(orcl.capex, 0) +
    " (" + fmtPct(orcl.capex / orcl.capexBase - 1, 0) + "), Microsoft at " + fmtB(msft.capex, 0) + " (" + fmtPct(msft.capex / msft.capexBase - 1, 0) + ") and Amazon at " + fmtB(amzn.capex, 0) + " (" + fmtPct(amzn.capex / amzn.capexBase - 1, 0) +
    "). Concentration is the transmission mechanism — it is why a single counterparty failure is not diversified away.");
  p.push("<b>Step 3 — the cost of money.</b> With the 10-year at " + S.y10.toFixed(2) + "%, the 20-year at " + S.y20.toFixed(2) + "% and the 30-year at " + S.y30.toFixed(2) +
    "%, Microsoft funds marginal capex at " + (msft.wacd * 100).toFixed(2) + "% while Oracle pays " + (orcl.wacd * 100).toFixed(2) + "% and SpaceX/xAI " + (spcx.wacd * 100).toFixed(2) +
    "%. That gap of " + Math.round((orcl.wacd - msft.wacd) * 10000) + "bp between AAA and BBB- is not cosmetic: at " + fmtB(orcl.newDebt, 0) + " of new issuance it is a real annual earnings difference, and it is why the rate slider moves Oracle's capex far more than Microsoft's. System-wide, interest on new debt runs " +
    fmtB(intr, 1) + " (" + fmtB(intr - intrB, 1) + " vs base).");
  p.push("<b>Step 4 — the supply chain.</b> System capex of " + fmtB(r.sysCapex, 0) + " converts into " + fmtB(supRev, 0) +
    " of supplier revenue, " + fmtB(supRev - supRevB, 0) + " versus base. The extremes: <b>" + best.n + "</b> at " + fmtPct(best.rev / best.revBase - 1, 0) +
    " and <b>" + worst.n + "</b> at " + fmtPct(worst.rev / worst.revBase - 1, 0) + ".");

  let gasRev = 0, gasRevB = 0, utRev = 0, utRevB = 0;
  r.sup.forEach((s) => { if (s.t === "gas") { gasRev += s.rev; gasRevB += s.revBase; } if (s.t === "util") { utRev += s.rev; utRevB += s.revBase; } });
  const wmb = r.sup.filter((s) => s.n === "Williams")[0];
  p.push("<b>Step 5 — gas versus grid.</b> At " + Math.round(S.gas * 100) + "% gas share of incremental data-centre power, the midstream and producer block runs " + fmtB(gasRev, 0) +
    " (" + fmtPct(gasRev / gasRevB - 1, 0) + ") while utilities and IPPs run " + fmtB(utRev, 0) + " (" + fmtPct(utRev / utRevB - 1, 0) + "). Williams specifically lands at " + fmtPct(wmb.rev / wmb.revBase - 1, 0) +
    " — it is the purest listed expression of the gas-to-power thesis. Note the trade-off this slider makes explicit: gas share and grid share are the same dollar. Raising one lowers the other.");
  p.push("<b>Step 6 — why the energy chain does not break the way silicon does.</b> Utilities sit on 20-year PPAs; grid equipment makers sit on multi-year backlogs. Those cushions mean a capex collapse shows up in <i>new orders</i> long before it shows up in <i>revenue</i>. Silicon has no cushion and reprices immediately. But utilities and midstream carry the opposite exposure: they are the most capital-intensive names here, so they lose on the rate slider what they are protected from on the capex slider.");

  const locked = r.hyp.reduce((a, x) => a + x.lockedDep, 0);
  p.push("<b>Step 7 — the asymmetry that matters.</b> If a lab defaults, the hyperscaler keeps the depreciating asset and loses the revenue. The prior cohort is already in service: at a " + S.life +
    "-year life it alone generates " + fmtB(locked, 0) + " of annual depreciation that <i>cannot be switched off by cutting capex</i>. Total system depreciation here is " + fmtB(dep, 0) +
    ", plus " + fmtB(intr, 1) + " of interest that does not care about utilisation either. Cut capex hard and watch depreciation barely move — that is the depreciation wall, and it is the difference between a demand shock and a solvency event.");

  document.getElementById("narrative")!.innerHTML = p.map((x) => '<p style="margin:0 0 12px">' + x + "</p>").join("");
}

// ---- assumptions (editable) ----
function renderAssumptions(): void {
  let h = "<thead><tr><th>Company</th><th>Source</th><th>Rating</th><th>Base rev $B</th><th>AI/DC exp.</th><th>Op margin</th><th>Op lev.</th><th>Cost sens.</th><th>Backlog cushion</th><th>Capex intensity</th><th>Spread bp</th></tr></thead><tbody>";
  world.SUP.forEach((s, i) => {
    h += "<tr><td>" + s.n + "</td><td>" + provBadge(supSource(s.n)) + '</td><td class="neu">' + s.rt + "</td>" +
      (["rev", "dc", "m", "lev", "cs", "cu", "cap", "spread"] as const).map((f) => {
        const st = f === "rev" ? 0.1 : f === "spread" ? 5 : 0.01;
        return '<td><input type="number" step="' + st + '" value="' + s[f] + '" data-i="' + i + '" data-f="' + f + '" class="asup"></td>';
      }).join("") + "</tr>";
  });
  document.getElementById("assumeSup")!.innerHTML = h + "</tbody>";

  let g = "<thead><tr><th>Company</th><th>Source</th><th>Rating</th><th>2026 capex $B</th><th>2025 cohort $B</th><th>AI rev $B</th><th>Base profit $B</th><th>Capex leverage</th><th>Incr. margin</th><th>Debt-funded</th><th>Spread bp</th></tr></thead><tbody>";
  world.HYP.forEach((x, i) => {
    g += "<tr><td>" + x.name + "</td><td>" + provBadge(hypSource(x.id)) + '</td><td class="neu">' + x.rating + "</td>" +
      (["capex", "prior", "aiRev", "baseProfit", "lev", "incM", "debtShare", "spread"] as const).map((f) => {
        const st = f === "spread" ? 5 : 0.01;
        return '<td><input type="number" step="' + st + '" value="' + x[f] + '" data-i="' + i + '" data-f="' + f + '" class="ahyp"></td>';
      }).join("") + "</tr>";
  });
  document.getElementById("assumeHyp")!.innerHTML = g + "</tbody>";

  let c = "";
  (Object.keys(world.COEF) as Array<keyof World["COEF"]>).forEach((k) => {
    const co = world.COEF[k];
    c += '<div class="srow"><span style="flex:1"><b style="font-weight:500">' + co.lab + '</b><div style="font-size:11.5px;color:var(--ink3);line-height:1.45">' + co.hint + "</div></span>" +
      '<input type="number" step="0.01" value="' + co.v + '" data-k="' + k + '" class="acoef" style="margin-left:12px"></div>';
  });
  document.getElementById("coefs")!.innerHTML = c;

  document.getElementById("sources")!.innerHTML =
    "<b>Live vs. estimated.</b> Treasury yields and the credit-spread multiple refresh daily from FRED and open at their live values; every row carries a provenance badge (LIVE / FILED / GUIDANCE / EST / STALE / USER). Base revenues are 2026 guidance or run-rate where disclosed. AI/data-centre exposure, operating leverage, backlog cushions and most credit spreads are estimates — override them freely (that flips the row to USER).<br><br>" +
    "<b>Estimates you should challenge</b>: every AI/data-centre exposure percentage; operating leverage and cost sensitivity; backlog cushions; credit spreads for names other than Oracle; NVIDIA base revenue; the 25% base gas share of marginal data-centre power; OpenAI and Anthropic compute spend — these are commitments and press reports, not audited capex; neither company files with the SEC and nothing here presents them as FILED.<br><br>" +
    "<b>What this model does not do</b>: no second-order equity or credit market feedback; no supplier-of-supplier depth; no commodity price model; annual snapshot only; hyperscaler profit is a delta off a stated base, not a rebuilt income statement; the AI labs are modelled as demand, not entities with their own balance sheets.";

  document.querySelectorAll<HTMLInputElement>(".asup").forEach((el) => {
    el.onchange = () => {
      const i = +el.dataset.i!, f = el.dataset.f! as keyof World["SUP"][number];
      (world.SUP[i] as any)[f] = parseFloat(el.value) || 0;
      userOverrides.add("sup:" + world.SUP[i].n + ":" + f);
      renderAssumptions();
      render();
    };
  });
  document.querySelectorAll<HTMLInputElement>(".ahyp").forEach((el) => {
    el.onchange = () => {
      const i = +el.dataset.i!, f = el.dataset.f!;
      (world.HYP[i] as any)[f] = parseFloat(el.value) || 0;
      userOverrides.add("hyp:" + world.HYP[i].id + ":" + f);
      renderAssumptions();
      render();
    };
  });
  document.querySelectorAll<HTMLInputElement>(".acoef").forEach((el) => {
    el.onchange = () => {
      const k = el.dataset.k! as keyof World["COEF"];
      world.COEF[k].v = parseFloat(el.value) || 0;
      userOverrides.add("coef:" + k);
      render();
    };
  });
}

// ---- header: as_of line + STALE banner ----
function renderHeader(snap: SnapshotPayload): void {
  let asof = document.getElementById("asof");
  if (!asof) {
    asof = document.createElement("div");
    asof.id = "asof";
    asof.className = "sub";
    const sub = document.querySelector("header .sub");
    sub?.parentNode?.insertBefore(asof, sub.nextSibling);
  }
  const live = snap.provenance.yields.y10.source === "LIVE" ? "live" : "seed";
  asof.textContent = "Data as of " + snap.asOf.slice(0, 10) + " (" + live + ") · generated " + snap.generatedAt.slice(0, 19).replace("T", " ") + "Z";

  let banner = document.getElementById("banner");
  if (!banner) {
    banner = document.createElement("div");
    banner.id = "banner";
    const wrap = document.querySelector(".wrap");
    wrap?.insertBefore(banner, wrap.firstChild);
  }
  if (snap.stale) {
    banner.setAttribute("style", "display:block;background:#d03b3b;color:#fff;padding:10px 16px;border-radius:8px;margin:0 0 16px;font-size:13.5px;font-weight:500");
    banner.textContent = "⚠ Some live fields are STALE — a data refresh failed; showing last-known values. Check the provenance badges.";
  } else {
    banner.setAttribute("style", "display:none");
  }
}

// ---- cross-module bridge: the CTA into /scenarios ----
function renderCTA(r: Result): void {
  const link = document.getElementById("ctaLink") as HTMLAnchorElement | null;
  if (!link) return;
  const b = bridge(r.cx);
  const econInputs = {
    capabilities: b.capabilities_default,
    adoption: b.adoption_default,
    autonomy: b.autonomy_default,
    productivity: b.productivity_default,
    adjustment_months: 12,
    new_task_share: 0.05,
    horizon_year: 2030,
  };
  const o = solveEcon(econInputs, econBaseline);
  const q = "from=capex&" + capexToParams(S).map(([k, v]) => k + "=" + v).join("&");
  link.href = "/scenarios?" + q;

  const chip = document.getElementById("xchip");
  if (chip) chip.innerHTML = "Implied GDP uplift: " + fmtPct(o.gdp_uplift_pct) + ' <span class="tag">EST · bridged</span>';
  const sub = document.getElementById("ctaSub");
  if (sub) {
    sub.textContent =
      "Your scenario: capex at " + Math.round(S.capex * 100) + "% of base · system capex $" + r.sysCapex.toFixed(0) +
      "B. This link is a judgement call (ESTIMATE), not an empirical estimate.";
  }
}

/** Keep the URL carrying the capex scenario so a copied link reproduces it. */
function syncUrl(): void {
  const q = capexToParams(S).map(([k, v]) => k + "=" + v).join("&");
  history.replaceState(null, "", location.pathname + "?" + q);
}

// ---- top-level render ----
function render(): void {
  const r = solve(S, world);
  renderKPIs(r);
  renderPrincipleThree(r);
  renderCurve();
  renderLadder(r);
  renderFlow(r);
  renderRevChart(r);
  renderBetas();
  renderTable(r);
  renderNarrative(r);
  renderCTA(r);
  syncUrl();
}

function sameDrivers(a: Drivers, b: Drivers): boolean {
  return (Object.keys(a) as Array<keyof Drivers>).every((k) => Math.abs(a[k] - b[k]) < 1e-9);
}

async function boot(): Promise<void> {
  let snap: SnapshotPayload;
  try {
    const res = await fetch("/api/snapshot");
    if (!res.ok) throw new Error("http " + res.status);
    snap = (await res.json()) as SnapshotPayload;
  } catch {
    snap = coldStartSnapshot(new Date().toISOString());
    snap.stale = true; // couldn't reach the API — be honest
  }
  world = snap.world;
  S = { ...snap.drivers };
  SCEN = snap.scenarios;
  provenance = snap.provenance;
  econBaseline = baselineValues(snap.econ ?? coldStartBaseline());

  // Restore a shared scenario from the URL (from a copied link or the return
  // path), defensively — unknown/out-of-range params are ignored/clamped.
  const q = new URLSearchParams(location.search);
  if (["cx", "ai", "oa", "an", "y10", "y20", "y30", "sm", "ct", "pw", "gs", "lf"].some((k) => q.has(k))) {
    S = capexFromParams(q, { ...snap.drivers });
  }
  active = sameDrivers(S, SCEN.base) ? "base" : "custom";

  const copy = document.getElementById("copylink");
  if (copy) {
    (copy as HTMLElement).onclick = () => {
      navigator.clipboard?.writeText(location.href);
      const t = copy.textContent;
      copy.textContent = "Copied ✓";
      setTimeout(() => (copy.textContent = t), 1400);
    };
  }

  const reset = document.getElementById("reset");
  if (reset) {
    (reset as HTMLElement).onclick = () => {
      S = { ...SCEN.base };
      active = "base";
      for (const x of [...userOverrides]) if (x.startsWith("driver:")) userOverrides.delete(x);
      buildControls();
      renderChips();
      render();
    };
  }

  renderHeader(snap);
  buildControls();
  renderChips();
  renderAssumptions();
  render();
  window.addEventListener("resize", () => renderFlow(solve(S, world)));
}

boot();
